import { useEffect } from 'react'
import { ApiError } from '../services'

interface NotificationProps {
  type: 'success' | 'error' | 'info' | 'warning'
  message: string
  onClose?: () => void
  autoClose?: boolean
  autoCloseDelay?: number
}

export const Notification = ({
  type,
  message,
  onClose,
  autoClose = false,
  autoCloseDelay = 5000
}: NotificationProps) => {
  useEffect(() => {
    if (autoClose && onClose) {
      const timer = setTimeout(onClose, autoCloseDelay)
      return () => clearTimeout(timer)
    }
  }, [autoClose, onClose, autoCloseDelay])

  const getStyles = () => {
    const baseStyles = "p-4 rounded-lg border flex items-center justify-between"

    switch (type) {
      case 'success':
        return `${baseStyles} bg-green-50 border-green-200 text-green-800`
      case 'error':
        return `${baseStyles} bg-red-50 border-red-200 text-red-800`
      case 'warning':
        return `${baseStyles} bg-yellow-50 border-yellow-200 text-yellow-800`
      case 'info':
        return `${baseStyles} bg-blue-50 border-blue-200 text-blue-800`
      default:
        return `${baseStyles} bg-gray-50 border-gray-200 text-gray-800`
    }
  }

  const getIcon = () => {
    switch (type) {
      case 'success':
        return (
          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        )
      case 'error':
        return (
          <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        )
      case 'warning':
        return (
          <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        )
      case 'info':
        return (
          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )
      default:
        return null
    }
  }

  return (
    <div className={getStyles()}>
      <div className="flex items-center">
        {getIcon()}
        <span className="ml-3">{message}</span>
      </div>
      {onClose && (
        <button
          onClick={onClose}
          className="ml-4 text-gray-400 hover:text-gray-600 focus:outline-none"
          aria-label="Fechar notificação"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  )
}

interface ErrorNotificationProps {
  error: ApiError | null | string
  onClose?: () => void
}

export const ErrorNotification = ({ error, onClose }: ErrorNotificationProps) => {
  if (!error) return null

  let message = ''
  let statusCode = ''

  if (typeof error === 'string') {
    message = error
  } else if (error && typeof error === 'object') {
    message = error.message || 'Erro desconhecido'
    statusCode = error.statusCode ? ` (${error.statusCode})` : ''
  }

  // Formatar mensagem para melhor legibilidade
  const formattedMessage = String(message || 'Erro desconhecido')
    .replace(/([A-Z])/g, ' $1') // Adicionar espaço antes de maiúsculas
    .replace(/^./, str => str.toUpperCase()) // Primeira letra maiúscula
    .replace(/\s+/g, ' ') // Remover espaços duplos
    .trim()

  return (
    <Notification
      type="error"
      message={`${formattedMessage}${statusCode}`}
      onClose={onClose}
      autoClose={!onClose}
      autoCloseDelay={8000}
    />
  )
}

interface SuccessNotificationProps {
  message: string
  onClose?: () => void
}

export const SuccessNotification = ({ message, onClose }: SuccessNotificationProps) => {
  return (
    <Notification
      type="success"
      message={message}
      onClose={onClose}
      autoClose={!onClose}
      autoCloseDelay={5000}
    />
  )
}

interface FormMessageProps {
  type: 'success' | 'error'
  message: string
  onClose?: () => void
}

export const FormMessage = ({ type, message, onClose }: FormMessageProps) => {
  return (
    <div className={`mb-4 p-4 rounded-lg border ${
      type === 'success'
        ? 'bg-green-50 border-green-200'
        : 'bg-red-50 border-red-200'
    }`}>
      <div className="flex items-start">
        <div className="flex-shrink-0">
          {type === 'success' ? (
            <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 011.414 0l4-4z" clipRule="evenodd" />
            </svg>
          ) : (
            <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          )}
        </div>
        <div className="ml-3 flex-1">
          <h3 className={`text-sm font-medium ${
            type === 'success' ? 'text-green-800' : 'text-red-800'
          }`}>
            {type === 'success' ? 'Operação realizada!' : 'Erro na operação'}
          </h3>
          <div className={`mt-2 text-sm ${
            type === 'success' ? 'text-green-700' : 'text-red-700'
          }`}>
            <p className="whitespace-pre-line">{message}</p>
          </div>
        </div>
        {onClose && (
          <div className="ml-auto pl-3">
            <button
              onClick={onClose}
              className={`inline-flex rounded-md p-1.5 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                type === 'success'
                  ? 'text-green-500 hover:bg-green-100 focus:ring-green-600'
                  : 'text-red-500 hover:bg-red-100 focus:ring-red-600'
              }`}
            >
              <span className="sr-only">Fechar</span>
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
